import { ResourceIdentifier } from '@cube3/common/model/resource-types';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { Folder } from '@cube3/common/model/schema/resources/folder';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { useMemo } from 'react';

export type NodeTreeType = {
  [id: string]: (Asset | Folder)[];
};

const emptyArray = [];

export const useReviewNodes = (
  rootNodeId: string,
  assetsOnly = false,
  maxItems = -1
) => {
  const { resources: nodes } = useResourceList__ALPHA({
    resourceId: undefined,
    resourceType: undefined,
    edgeType: rootNodeId ? 'content-tree-node' : undefined,
    edgeLabel: 'nodes',
    params: useMemo(
      () => ({
        filter: {
          ancestor: rootNodeId
        },
        page: { size: maxItems }
      }),
      [rootNodeId, maxItems]
    ),
    strategy: 'fetch-on-mount'
  });

  const resourceIdentifiers = useMemo(() => {
    if (assetsOnly || !nodes) return [];
    return nodes
      .map((node) => node.relationships.resource)
      .filter((r) => r.type === 'folder') as ResourceIdentifier<'folder'>[];
  }, [nodes, assetsOnly]);

  const { resources: folders } = useResource__ALPHA({
    resourceIdentifiers: resourceIdentifiers
  });

  const params = useMemo(() => {
    if (!nodes?.length) {
      return;
    }
    const assetIds =
      nodes
        ?.filter((node) => node.relationships.resource.type === 'asset')
        ?.map((node) => node.relationships.resource.id) || [];

    return {
      filter: {
        ids: assetIds.length ? assetIds.join(',') : undefined
      },
      page: { size: -1 }
    };
  }, [nodes]);

  const { resources: assets, status: assetStatus } = useResourceList__ALPHA({
    edgeType: params?.filter.ids ? 'asset' : undefined,
    edgeLabel: 'assets',
    params
  });

  return {
    folders,
    // NOTE: return undefined if assets haven't been resolved. Return empty array if nodelist doesn't contain asset type nodes
    assets:
      params && !params.filter.ids
        ? emptyArray
        : assetStatus === statuses.SUCCESS
        ? assets
        : undefined
  };
};
